import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  TextField,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  InputAdornment,
  Grid,
  Button,
  IconButton,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  SelectChangeEvent,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Autocomplete,
} from '@mui/material';
import { Search as SearchIcon, Add as AddIcon, Edit as EditIcon, Delete as DeleteIcon, CallMerge as CallMergeIcon } from '@mui/icons-material';
import { UniversalPagination } from '../../components/common/UniversalPagination';
import {
  medicationService,
  MedicationResponseDto,
  GetMedicationsParams,
  MedicationForm,
  MedicationSourceType,
  Locale,
  MergeMedicationDto,
} from '../../services/medication.service';
import { useDebounce } from '../../hooks/useDebounce';
import { toastService } from '../../services/toast.service';
import { ConfirmationDialog } from '@components/common/ConfirmationDialog.tsx';

export const MedicationsTab: React.FC = () => {
  const [medications, setMedications] = useState<MedicationResponseDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [limit, setLimit] = useState(10);

  const [searchQuery, setSearchQuery] = useState('');
  const debouncedSearchQuery = useDebounce(searchQuery, 500);

  const [formFilter, setFormFilter] = useState<MedicationForm | ''>('');
  const [dosageFilter, setDosageFilter] = useState<string>('');
  const [sourceTypeFilter, setSourceTypeFilter] = useState<MedicationSourceType | ''>('all');

  const [isCreateModalOpen, setCreateModalOpen] = useState(false);
  const [isEditModalOpen, setEditModalOpen] = useState(false);
  const [medicationToEdit, setMedicationToEdit] = useState<MedicationResponseDto | null>(null);

  const [isConfirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
  const [medicationToDelete, setMedicationToDelete] = useState<MedicationResponseDto | null>(null);

  const [isMergeModalOpen, setMergeModalOpen] = useState(false);
  const [medicationToMerge, setMedicationToMerge] = useState<MedicationResponseDto | null>(null);
  const [mergeTarget, setMergeTarget] = useState<MedicationResponseDto | null>(null);
  const [systemMedications, setSystemMedications] = useState<MedicationResponseDto[]>([]);
  const [systemMedicationsLoading, setSystemMedicationsLoading] = useState(false);

  const [currentMedicationNameI18n, setCurrentMedicationNameI18n] = useState<{[key in Locale]?: string}>({});
  const [currentMedicationForm, setCurrentMedicationForm] = useState<MedicationForm>('tablets');
  const [currentMedicationDosage, setCurrentMedicationDosage] = useState<string[]>(['25mg']);

  const locales: Locale[] = ['uk', 'en', 'ru', 'cs', 'es', 'pl', 'be'];

  const fetchMedications = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params: GetMedicationsParams = {
        page,
        limit,
        search: debouncedSearchQuery || undefined,
        sortBy: 'name',
        sortOrder: 'ASC',
        form: formFilter || undefined,
        sourceType: sourceTypeFilter || undefined,
      };
      const response = await medicationService.getMedications(params);
      setMedications(response.data);
      if (response.meta && response.meta.total && response.meta.limit) {
        setTotalPages(Math.ceil(response.meta.total / response.meta.limit));
      } else {
        setTotalPages(0);
      }
    } catch (err) {
      toastService.error('Не вдалося завантажити список препаратів.');
      setError('Не вдалося завантажити список препаратів.');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [page, limit, debouncedSearchQuery, formFilter, dosageFilter, sourceTypeFilter]);

  useEffect(() => {
    fetchMedications();
  }, [fetchMedications]);

  const fetchSystemMedications = useCallback(async (search: string) => {
    setSystemMedicationsLoading(true);
    try {
      const params: GetMedicationsParams = {
        limit: 20,
        search: search || undefined,
        sourceType: 'system',
      };
      const response = await medicationService.getMedications(params);
      setSystemMedications(response.data);
    } catch (err) {
      toastService.error('Не вдалося завантажити системні препарати.');
    } finally {
      setSystemMedicationsLoading(false);
    }
  }, []);

  const handlePageChange = (value: number) => setPage(value);
  const handleLimitChange = (value: number) => { setLimit(value); setPage(1); };
  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(event.target.value);
    setPage(1);
  };
  const handleFormFilterChange = useCallback((event: SelectChangeEvent<MedicationForm | ''>) => {
    setFormFilter(event.target.value as MedicationForm | '');
    setPage(1);
  }, []);
  const handleDosageFilterChange = useCallback((event: SelectChangeEvent<string>) => {
    setDosageFilter(event.target.value);
    setPage(1);
  }, []);
  const handleSourceTypeFilterChange = useCallback((event: SelectChangeEvent<MedicationSourceType | ''>) => {
    setSourceTypeFilter(event.target.value as MedicationSourceType | '');
    setPage(1);
  }, []);

  const handleOpenCreateModal = () => {
    setCurrentMedicationNameI18n(locales.reduce((acc, loc) => ({ ...acc, [loc]: '' }), {}));
    setCurrentMedicationForm('tablets');
    setCurrentMedicationDosage(['25mg']);
    setCreateModalOpen(true);
  };

  const handleCreateMedication = async () => {
    setLoading(true);
    try {
      await medicationService.createMedication({
        nameI18n: currentMedicationNameI18n,
        form: currentMedicationForm,
        dosage: currentMedicationDosage,
      });
      toastService.success('Препарат успішно створено!');
      setCreateModalOpen(false);
      fetchMedications();
    } catch (err: any) {
      toastService.error(`Помилка створення препарату: ${err.message || 'Невідома помилка'}`);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenEditModal = async (medication: MedicationResponseDto) => {
    setLoading(true);
    setMedicationToEdit(medication);
    try {
      const editData = await medicationService.getMedicationForEdit(medication.id);
      setCurrentMedicationNameI18n(editData.nameI18n);
      setCurrentMedicationForm(editData.form);
      setCurrentMedicationDosage(editData.dosage || []);
      setEditModalOpen(true);
    } catch (err) {
      toastService.error('Не вдалося завантажити дані для редагування.');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateMedication = async () => {
    if (!medicationToEdit) return;
    setLoading(true);
    try {
      await medicationService.updateMedication(medicationToEdit.id, {
        nameI18n: currentMedicationNameI18n,
        form: currentMedicationForm,
        dosage: currentMedicationDosage,
      });
      toastService.success('Препарат успішно оновлено!');
      setEditModalOpen(false);
      setMedicationToEdit(null);
      fetchMedications();
    } catch (err: any) {
      toastService.error(`Помилка оновлення препарату: ${err.message || 'Невідома помилка'}`);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDeleteConfirm = (medication: MedicationResponseDto) => {
    setMedicationToDelete(medication);
    setConfirmDeleteOpen(true);
  };

  const handleDeleteMedication = async () => {
    if (!medicationToDelete) return;
    setLoading(true);
    try {
      await medicationService.deleteMedication(medicationToDelete.id);
      toastService.success(`Препарат "${medicationToDelete.name}" успішно видалено.`);
      setConfirmDeleteOpen(false);
      setMedicationToDelete(null);
      fetchMedications();
    } catch (err: any) {
      toastService.error(`Помилка видалення препарату: ${err.message || 'Невідома помилка'}`);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenMergeModal = (medication: MedicationResponseDto) => {
    setMedicationToMerge(medication);
    setMergeTarget(null);
    setSystemMedications([]);
    setMergeModalOpen(true);
  };

  const handleMergeMedication = async () => {
    if (!medicationToMerge || !mergeTarget) return;
    setLoading(true);
    try {
      const mergeDto: MergeMedicationDto = {
        sourceMedicationId: medicationToMerge.id,
        targetMedicationId: mergeTarget.id,
      };
      const result = await medicationService.mergeMedications(mergeDto);
      toastService.success(`Препарат '${result.removedMedication.name}' об'єднано з '${result.targetMedication.name}'. Оновлено подій: ${result.updatedCalendarEvents}.`);
      setMergeModalOpen(false);
      fetchMedications();
    } catch (err: any) {
      toastService.error(`Помилка злиття: ${err.message || 'Невідома помилка'}`);
    } finally {
      setLoading(false);
    }
  };

  const medicationForms: MedicationForm[] = ['tablets', 'capsules', 'syrup', 'drops', 'solution', 'cream', 'ointment', 'powder', 'spray', 'other'];
  const medicationDosages: string[] = ['25mg', '50mg', '100mg', '250mg', '500mg', '1000mg', '400iu', '1000iu', '2000iu', '5ml', '10ml', 'other'];
  const medicationSourceTypes: MedicationSourceType[] = ['all', 'system', 'user'];

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Button variant="contained" startIcon={<AddIcon />} onClick={handleOpenCreateModal}>Додати препарат</Button>
      </Box>

      <Paper sx={{ p: 2, mt: 2 }}>
        <Grid container spacing={2} sx={{ mb: 2 }} alignItems="center">
          <Grid item xs={12} sm={6} md={4}><TextField fullWidth label="Пошук за назвою" value={searchQuery} onChange={handleSearchChange} InputProps={{ startAdornment: (<InputAdornment position="start"><SearchIcon /></InputAdornment>) }} /></Grid>
          <Grid item xs={12} sm={6} md={2}><FormControl fullWidth><InputLabel>Форма</InputLabel><Select value={formFilter} label="Форма" onChange={handleFormFilterChange}><MenuItem value=""><em>Всі</em></MenuItem>{medicationForms.map(f => <MenuItem key={f} value={f}>{f}</MenuItem>)}</Select></FormControl></Grid>
          <Grid item xs={12} sm={6} md={2}><FormControl fullWidth><InputLabel>Дозування</InputLabel><Select value={dosageFilter} label="Дозування" onChange={handleDosageFilterChange}><MenuItem value=""><em>Всі</em></MenuItem>{medicationDosages.map(d => <MenuItem key={d} value={d}>{d}</MenuItem>)}</Select></FormControl></Grid>
          <Grid item xs={12} sm={6} md={2}><FormControl fullWidth><InputLabel>Джерело</InputLabel><Select value={sourceTypeFilter} label="Джерело" onChange={handleSourceTypeFilterChange}>{medicationSourceTypes.map(t => <MenuItem key={t} value={t}>{t === 'all' ? 'Всі' : t === 'system' ? 'Система' : 'Користувач'}</MenuItem>)}</Select></FormControl></Grid>
        </Grid>

        {error ? <Typography color="error" textAlign="center" sx={{ mt: 4 }}>{error}</Typography> : loading ? <Box display="flex" justifyContent="center" sx={{ mt: 4 }}><CircularProgress /></Box> : medications.length === 0 ? <Typography sx={{ mt: 4, textAlign: 'center' }}>Препаратів не знайдено.</Typography> : (
          <>
            <TableContainer><Table stickyHeader><TableHead><TableRow><TableCell>Назва</TableCell><TableCell>Форма</TableCell><TableCell>Дозування</TableCell><TableCell>Джерело</TableCell><TableCell align="right">Дії</TableCell></TableRow></TableHead><TableBody>{medications.map((med) => (<TableRow hover key={med.id}><TableCell>{med.name}</TableCell><TableCell>{med.form}</TableCell><TableCell>{med.dosage?.join(', ')}</TableCell><TableCell>{med.createdByUserId ? 'Користувач' : 'Система'}</TableCell><TableCell align="right">{med.createdByUserId && <IconButton onClick={() => handleOpenMergeModal(med)} disabled={loading}><CallMergeIcon /></IconButton>}<IconButton onClick={() => handleOpenEditModal(med)} disabled={loading}><EditIcon /></IconButton><IconButton onClick={() => handleOpenDeleteConfirm(med)} disabled={loading}><DeleteIcon /></IconButton></TableCell></TableRow>))}</TableBody></Table></TableContainer>
            <UniversalPagination page={page} count={totalPages} limit={limit} onPageChange={handlePageChange} onLimitChange={handleLimitChange} storageKey="pagination:library-medications" />
          </>
        )}
      </Paper>

      <Dialog open={isCreateModalOpen} onClose={() => setCreateModalOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>Додати новий препарат</DialogTitle>
        <DialogContent><Box sx={{ pt: 1 }}>{locales.map(lang => <TextField key={lang} margin="dense" label={`Назва препарату (${lang.toUpperCase()})`} type="text" fullWidth variant="outlined" value={currentMedicationNameI18n[lang] || ''} onChange={e => setCurrentMedicationNameI18n({ ...currentMedicationNameI18n, [lang]: e.target.value })} required />)}<FormControl fullWidth margin="dense"><InputLabel>Форма</InputLabel><Select value={currentMedicationForm} label="Форма" onChange={e => setCurrentMedicationForm(e.target.value as MedicationForm)}>{medicationForms.map(f => <MenuItem key={f} value={f}>{f}</MenuItem>)}</Select></FormControl><FormControl fullWidth margin="dense"><InputLabel>Дозування</InputLabel><Select value={currentMedicationDosage} label="Дозування" onChange={e => setCurrentMedicationDosage(e.target.value as unknown as string[])} multiple>{medicationDosages.map(d => <MenuItem key={d} value={d}>{d}</MenuItem>)}</Select></FormControl></Box></DialogContent>
        <DialogActions><Button onClick={() => setCreateModalOpen(false)}>Скасувати</Button><Button onClick={handleCreateMedication} disabled={loading} variant="contained">{loading ? <CircularProgress size={24} /> : 'Створити'}</Button></DialogActions>
      </Dialog>

      <Dialog open={isEditModalOpen} onClose={() => setEditModalOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>Редагувати препарат</DialogTitle>
        <DialogContent><Box sx={{ pt: 1 }}>{locales.map(lang => <TextField key={lang} margin="dense" label={`Назва препарату (${lang.toUpperCase()})`} type="text" fullWidth variant="outlined" value={currentMedicationNameI18n[lang] || ''} onChange={e => setCurrentMedicationNameI18n({ ...currentMedicationNameI18n, [lang]: e.target.value })} required />)}<FormControl fullWidth margin="dense"><InputLabel>Форма</InputLabel><Select value={currentMedicationForm} label="Форма" onChange={e => setCurrentMedicationForm(e.target.value as MedicationForm)}>{medicationForms.map(f => <MenuItem key={f} value={f}>{f}</MenuItem>)}</Select></FormControl><FormControl fullWidth margin="dense"><InputLabel>Дозування</InputLabel><Select value={currentMedicationDosage} label="Дозування" onChange={e => setCurrentMedicationDosage(e.target.value as unknown as string[])} multiple>{medicationDosages.map(d => <MenuItem key={d} value={d}>{d}</MenuItem>)}</Select></FormControl></Box></DialogContent>
        <DialogActions><Button onClick={() => setEditModalOpen(false)}>Скасувати</Button><Button onClick={handleUpdateMedication} disabled={loading} variant="contained">{loading ? <CircularProgress size={24} /> : 'Зберегти зміни'}</Button></DialogActions>
      </Dialog>

      <ConfirmationDialog open={isConfirmDeleteOpen} onClose={() => setConfirmDeleteOpen(false)} onConfirm={handleDeleteMedication} title="Підтвердити видалення" message={`Ви впевнені, що хочете видалити препарат "${medicationToDelete?.name}"?`} confirmText="Видалити" />

      <Dialog open={isMergeModalOpen} onClose={() => setMergeModalOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>Об'єднати препарат</DialogTitle>
        <DialogContent>
          <Typography sx={{ mb: 2 }}>Ви збираєтеся об'єднати препарат <strong>{medicationToMerge?.name}</strong> з системним препаратом. Усі записи в календарі будуть оновлені, а цей препарат буде видалено.</Typography>
          <Autocomplete
            options={systemMedications}
            getOptionLabel={(option) => `${option.name} (${option.form}, ${option.dosage?.join(', ')})`}
            loading={systemMedicationsLoading}
            value={mergeTarget}
            onChange={(_event, newValue) => setMergeTarget(newValue)}
            onInputChange={(_event, newInputValue) => fetchSystemMedications(newInputValue)}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Пошук системного препарату"
                variant="outlined"
                InputProps={{ ...params.InputProps, endAdornment: <>{systemMedicationsLoading ? <CircularProgress color="inherit" size={20} /> : null}{params.InputProps.endAdornment}</> }}
              />
            )}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setMergeModalOpen(false)}>Скасувати</Button>
          <Button onClick={handleMergeMedication} disabled={!mergeTarget || loading} variant="contained">{loading ? <CircularProgress size={24} /> : "Об'єднати"}</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
